import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MemberDto } from '../api/types';
import { backdropClose } from '../lib/modalBackdrop';
import Avatar from './Avatar';

interface Props {
  groupTitle: string;
  /** Участники группы без самого владельца. */
  others: MemberDto[];
  busy?: boolean;
  error?: string;
  onTransferAndLeave: (newOwnerId: string) => void;
  onDeleteGroup: () => void;
  onCancel: () => void;
}

/** Кого предложить по умолчанию — как сервер выбирает автопреемника (ChatService.pickSuccessor):
 *  первый админ, иначе первый участник (список с сервера уже в порядке вступления). */
function defaultSuccessor(others: MemberDto[]): string | null {
  return (others.find((m) => m.role === 2) ?? others[0])?.user.id ?? null;
}

/**
 * Владелец покидает группу: группа не должна остаться без владельца (раньше её после этого
 * никто не мог ни удалить, ни назначать админов). Выбор — передать права участнику и выйти,
 * либо удалить группу для всех. Без confirm()/alert() — кастомная модалка, как ConfirmModal.
 */
export default function OwnerLeaveModal({ groupTitle, others, busy, error, onTransferAndLeave, onDeleteGroup, onCancel }: Props) {
  const { t } = useTranslation('dialogs');
  const [selected, setSelected] = useState<string | null>(() => defaultSuccessor(others));
  const [confirmDelete, setConfirmDelete] = useState(false);
  return (
    <div className="modal-backdrop" {...backdropClose(onCancel)}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <h2>{t('ownerLeave.title', { groupTitle })}</h2>
        {confirmDelete ? (
          <>
            <p style={{ marginBottom: 16, fontSize: 15 }}>{t('ownerLeave.deleteQuestion')}</p>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(false)} disabled={busy}>{t('ownerLeave.back')}</button>
              <button className="btn" style={{ background: 'var(--error)' }} onClick={onDeleteGroup} disabled={busy}>
                {t('ownerLeave.deleteGroup')}
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ marginBottom: 12, fontSize: 14, color: 'var(--muted)' }}>
              {t('ownerLeave.prompt')}
            </p>
            <div style={{ maxHeight: 260, overflowY: 'auto', marginBottom: 12 }}>
              {others.map((m) => (
                <label
                  key={m.user.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px', cursor: 'pointer' }}
                >
                  <input
                    type="radio"
                    name="new-owner"
                    checked={selected === m.user.id}
                    onChange={() => setSelected(m.user.id)}
                  />
                  <Avatar userId={m.user.id} name={m.user.displayName} version={m.user.avatarFileId} size={28} />
                  <span style={{ flex: 1 }}>{m.user.displayName}</span>
                  {m.role === 2 && <span className="member-role-badge">{t('ownerLeave.adminRole')}</span>}
                </label>
              ))}
            </div>
            {error && <div className="error" style={{ marginBottom: 10 }}>{error}</div>}
            <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <button className="btn btn-ghost" style={{ color: 'var(--error)' }} onClick={() => setConfirmDelete(true)} disabled={busy}>
                {t('ownerLeave.deleteGroup')}
              </button>
              <div className="row" style={{ gap: 8 }}>
                <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>{t('actions.cancel')}</button>
                <button className="btn" onClick={() => selected && onTransferAndLeave(selected)} disabled={busy || !selected}>
                  {t('ownerLeave.transferAndLeave')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
